/**
 * Ontology 부분 그래프 시각화 (계획서 §10, §13.5, §13.6).
 *
 * `@neo4j-nvl/react` 로 그린다. 텍스트 목록은 눈에 잘 들어오지 않아 2026-09-04 에 그래프
 * 렌더링으로 바꿨다. 목록은 접어서 함께 남긴다 — 시각화가 실패해도 내용은 봐야 한다.
 *
 * Browser 는 Neo4j 에 직접 연결하지 않는다. FastAPI 가 준 부분 그래프만 그린다 (§10).
 */

import { useMemo, useState } from "react";
import { InteractiveNvlWrapper } from "@neo4j-nvl/react";
import type { Node, Relationship } from "@neo4j-nvl/base";

import type { OntologyNode, OntologyRelationship } from "@/types/api";

import { GraphErrorBoundary } from "./GraphErrorBoundary";

/** label 별 색. 색만으로 구분하지 않도록 범례와 클릭 시 속성 목록을 함께 둔다 (§13.1). */
const LABEL_COLORS: Record<string, string> = {
  Equipment: "#1a7f4b",
  ProcessStep: "#2f6fb0",
  Utility: "#7a5cb0",
  Batch: "#9a6200",
  Rule: "#5a6672",
  Incident: "#b3261e",
  Plan: "#1b2129",
  Strategy: "#0f766e",
};

const DEFAULT_COLOR = "#5a6672";

function labelOf(node: OntologyNode): string {
  return node.labels[0] ?? "Node";
}

/** node 를 사람이 읽는 이름으로 바꾼다.
 *
 * 실행 node 의 key 는 `PLAN:<uuid>:1` 처럼 uuid 가 들어가 그대로 쓰면 읽을 수 없다.
 * 속성에서 뜻이 통하는 값을 골라 쓴다.
 */
function shortCaption(node: OntologyNode): string {
  const props = node.properties;
  switch (labelOf(node)) {
    case "Plan":
      return `계획 v${props.plan_version ?? "?"}`;
    case "Strategy":
      return `${props.code ?? "?"}안`;
    case "Incident":
      return `고장 ${props.equipment_id ?? ""}`.trim();
    default:
      break;
  }
  if (props.name) return props.name;
  const raw = node.caption;
  const colon = raw.indexOf(":");
  return colon >= 0 ? raw.slice(colon + 1) : raw;
}

interface Props {
  nodes: OntologyNode[];
  relationships: OntologyRelationship[];
  /** 공정도 아래 패널처럼 좁은 자리에 넣을 때 설명과 목록을 줄인다. */
  compact?: boolean;
}

export function OntologyGraph({
  nodes,
  relationships,
  compact = false,
}: Props): React.JSX.Element {
  const [selected, setSelected] = useState<OntologyNode | null>(null);
  const [showList, setShowList] = useState(false);

  const nvlNodes: Node[] = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.id,
        // label 을 caption 에 함께 넣으면 잘려서 이름이 안 보인다. 종류는 색과 범례,
        // 그리고 node 를 클릭했을 때 나오는 속성 목록으로 알린다.
        caption: shortCaption(node),
        color: LABEL_COLORS[labelOf(node)] ?? DEFAULT_COLOR,
        // NVL 은 화면상 크기가 작으면 caption 을 감춘다. 물러서서 보여도 이름이
        // 보이도록 node 를 크게 잡는다.
        size: labelOf(node) === "Incident" ? 64 : 52,
      })),
    [nodes],
  );
  // 좁은 패널에서는 더 물러서야 그래프 전체가 들어온다.
  const initialZoom = compact ? 0.45 : 0.8;

  const nvlRels: Relationship[] = useMemo(
    () =>
      relationships.map((rel) => ({
        id: rel.id,
        from: rel.from,
        to: rel.to,
        caption: rel.type,
      })),
    [relationships],
  );

  const legend = useMemo(
    () => [...new Set(nodes.map(labelOf))].sort((a, b) => a.localeCompare(b)),
    [nodes],
  );

  return (
    <div className="ontology">
      <p className="hint">
        {compact
          ? `node ${String(nodes.length)}개 · 관계 ${String(relationships.length)}개 — 끌어서 보기`
          : `현재 사고와 관계된 부분 그래프입니다. node ${String(nodes.length)}개, 관계 ${String(relationships.length)}개. 끌어서 옮기고 휠로 확대할 수 있으며, node 를 클릭하면 속성이 나옵니다.`}
      </p>

      <ul className="graph-legend">
        {legend.map((label) => (
          <li key={label}>
            <span
              className="legend-dot"
              style={{ background: LABEL_COLORS[label] ?? DEFAULT_COLOR }}
              aria-hidden="true"
            />
            {label}
          </li>
        ))}
      </ul>

      <GraphErrorBoundary fallback={<RelationshipList nodes={nodes} relationships={relationships} />}>
        <div className={compact ? "graph-canvas is-compact" : "graph-canvas"}>
          <InteractiveNvlWrapper
            nodes={nvlNodes}
            rels={nvlRels}
            // nvl 1.x 부터 `layout` prop 은 deprecated 다. nvlOptions 안으로 옮겼다.
            // 부분 그래프가 화면 밖으로 나가지 않도록 조금 물러서서 시작한다.
            nvlOptions={{ initialZoom, renderer: "canvas", layout: "forceDirected" }}
            mouseEventCallbacks={{
              onZoom: true,
              onPan: true,
              onDrag: true,
              onNodeClick: (node: Node) => {
                setSelected(nodes.find((n) => n.id === node.id) ?? null);
              },
            }}
          />
        </div>
      </GraphErrorBoundary>

      {selected && (
        <dl className="graph-detail">
          <div>
            <dt>label</dt>
            <dd>{selected.labels.join(", ")}</dd>
          </div>
          <div>
            <dt>key</dt>
            <dd>{selected.caption}</dd>
          </div>
          {Object.entries(selected.properties)
            .filter(([key]) => key !== "key")
            .map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
      )}

      {!compact && (
      <button
        type="button"
        className="disclosure"
        aria-expanded={showList}
        onClick={() => {
          setShowList(!showList);
        }}
      >
        {showList ? "▾" : "▸"} 관계 목록 (텍스트)
      </button>
      )}
      {!compact && showList && (
        <RelationshipList nodes={nodes} relationships={relationships} />
      )}
    </div>
  );
}


/** 시각화가 실패해도 내용은 봐야 한다. 목록은 언제나 만들 수 있다. */
function RelationshipList({ nodes, relationships }: Props): React.JSX.Element {
  return (
    <ul className="ontology-rels">
      {relationships.map((rel) => {
        const from = nodes.find((n) => n.id === rel.from)?.caption ?? rel.from;
        const to = nodes.find((n) => n.id === rel.to)?.caption ?? rel.to;
        return (
          <li key={rel.id}>
            {from} —[{rel.type}]→ {to}
          </li>
        );
      })}
    </ul>
  );
}
